import { useLogger } from "@kiddocare-platform/portal-common/helpers";
import { Loader } from "@kiddocare-platform/portal-common/ui";
import React, { Suspense } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { useAdminContext } from "../contexts/admin.context";

const AuthPage = React.lazy(() => import('./auth/auth.page'));
const AuthCallbackPage = React.lazy(() => import('./auth/callback.page'));
const AuthLoginPage = React.lazy(() => import('./auth/login.page'));
const AuthLogoutPage = React.lazy(() => import('./auth/logout.page'));
const AuthPostLogoutPage = React.lazy(() => import('./auth/postlogout.page'));

const MainPage = React.lazy(() => import('./main.page'));

// DASHBOARD
const DashboardHomePage = React.lazy(() => import('./dashboards/home.page'));

// PEDIATRICIANS
const PediatriciansPage = React.lazy(() => import('./pediatricians/pediatricians.page'));
const PediatriciansDetailPage = React.lazy(() => import('./pediatricians/detail.page'));
const PediatricianCardPage = React.lazy(() => import('./pediatricians/card.page'));
const PediatriciansCertificatesPage = React.lazy(() => import('./pediatricians/certificates.page'));
const PediatriciansBillingsPage = React.lazy(() => import('./pediatricians/billings.page'));
const PediatricianPatientsPage = React.lazy(() => import('./pediatricians/patients.page'));
const BillingPage = React.lazy(() => import('./billing/billing.page'));
const BillingSubscriptionPage = React.lazy(() => import('./billing/subscriptions.page'));

// PROFILE
const ProfileInfoPage = React.lazy(() => import('./profile/info.page'));

// CORE
const CorePage = React.lazy(() => import('./core/core.page'));

// PLATFORM
const PlatformPage = React.lazy(() => import('./platform/platform.page'));
const PlatformOrbitFlowPage = React.lazy(() => import('./platform/orbit/flow.page'));
const PlatformOrbitQueuePage = React.lazy(() => import('./platform/orbit/queue.page'));
const PlatformOrbitQueuesPage = React.lazy(() => import('./platform/orbit/queues.page'));
const PlatformOrbitRedisPage = React.lazy(() => import('./platform/orbit/redis.page'));
//const PlatformAtlasPage = React.lazy(() => import('./platform/atlas/atlas.page'));
const PlatformAtlasContentPage = React.lazy(() => import('./platform/atlas/content.page'));
const PlatformAtlasScopesPage = React.lazy(() => import('./platform/atlas/scopes.page'));
//const PlatformAtlasElementPage = React.lazy(() => import('./platform/atlas/element.page'));

const ProtectedRoute: React.FC = () => {
  const log = useLogger(PagesRoutes.name);
  const { isInitialized, user } = useAdminContext();

  //log.debug("[ProtectedRoute] Rendering ProtectedRoute, user:", user?.name, "settings:", settings?.length, "isInitialized:", isInitialized, "isSettingsLoaded:", isSettingsLoaded);

  if (!isInitialized) {
    log.warn("[ProtectedRoute] App not initialized");
    return <Loader mode="overlay" isShowing={true} />;
  }

  if (!user) {
    log.warn("[ProtectedRoute] User not authenticated");
    return <Navigate to="/auth/login" replace />;
  }

  /*if (!isSettingsLoaded) {
    log.warn("[ProtectedRoute] Settings not loaded");
    return <Loader mode="overlay" isShowing={true} />;
  }

  if (settings && settings.length > 0) {
    log.warn("[ProtectedRoute] User has not completed settings");
    return <Navigate to="/settings" replace />;
  }*/

  return <Outlet />;
};

const PagesRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<Loader mode="overlay" />}>
        <Routes>

          <Route path="/auth" element={<AuthPage />}>
            <Route path="login" element={<AuthLoginPage />} />
            <Route path="logout" element={<AuthLogoutPage />} />
            <Route path="callback" element={<AuthCallbackPage />} />
            <Route path="postlogout" element={<AuthPostLogoutPage />} />
          </Route>

          {/*<Route path="/settings" element={<SettingsPage />}>
            <Route index element={<Navigate to="intro" replace />} />
            <Route path="intro" element={<SettingsIntroPage />} />
            <Route path="personal-data" element={<PersonalDataPage />} />
            <Route path="address-fiscal" element={<AddressFiscalPage />} />
            <Route path="load-document" element={<LoadDocumentPage />} />
            <Route path="professional-data" element={<ProfessionalDataPage />} />
            <Route path="policy-accepted" element={<PolicyAcceptedPage />} />
          </Route>*/}

          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<MainPage />}>

              <Route index element={<Navigate to="dashboards" replace />} />

              <Route path="dashboards">
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<DashboardHomePage />} />
              </Route>

              <Route path="pediatricians" element={<PediatriciansPage />} />
              <Route path="pediatricians/detail/:id" element={<PediatriciansDetailPage />}>
                <Route path="" element={<PediatricianCardPage />} />
                <Route path="certificates" element={<PediatriciansCertificatesPage />} />
                <Route path="billings" element={<PediatriciansBillingsPage />} />
                <Route path="patients" element={<PediatricianPatientsPage />} />
              </Route>

              <Route path="/billing" element={<BillingPage />} >
                <Route path="subscriptions" element={<BillingSubscriptionPage />} />
              </Route>

              <Route path='/platform' element={<PlatformPage />}>

                <Route path="orbit">
                  <Route path="flow" element={<PlatformOrbitFlowPage />} />
                  <Route path="queues" element={<PlatformOrbitQueuesPage />}>
                    <Route path=":queue" element={<PlatformOrbitQueuePage />} />
                  </Route>
                  <Route path="redis" element={<PlatformOrbitRedisPage />} />
                  <Route index element={<Navigate to="queues" replace />} />
                </Route>

                <Route path='atlas'>
                  <Route path="scopes" element={<PlatformAtlasScopesPage />} />
                  <Route path=":scope/*" element={<PlatformAtlasContentPage />} />
                  <Route index element={<Navigate to="scopes" replace />} />
                </Route>
              </Route>

              <Route path='/core' element={<CorePage />}>
              
              </Route>

              {/*<Route path='/platform/orbit' element={<PlatformOrbitPage />}>
                <Route path="flow" element={<PlatformOrbitFlowPage />} />
                <Route path="queue/:queue" element={<PlatformOrbitQueuePage />} />
                <Route path="queues" element={<PlatformOrbitQueuesPage />} />
                <Route path="redis" element={<PlatformOrbitRedisPage />} />
                <Route index element={<Navigate to="queues" replace />} />
              </Route>

              <Route path='/platform/atlas' element={<PlatformAtlasPage />}>
                <Route path=":scope/*" element={<PlatformAtlasScopePage />} />
              </Route>*/}

              <Route path="/profile" element={<ProfileInfoPage />} />

              <Route path="*" element={<h1>404 - Pagina non trovata</h1>} />

            </Route>
          </Route>

        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export { PagesRoutes };
